import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import Block from './Block';
import { markdownTableComponents } from './TextBubble';
import { Button } from '../../components/ui/button';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
const API_ORIGIN = API_BASE_URL.replace(/\/api\/?$/, '');

function resolveUrl(url) {
  if (!url) return null;
  if (/^https?:\/\//i.test(url)) return url;
  if (url.startsWith('/')) return `${API_ORIGIN}${url}`;
  return url;
}

/**
 * Renders a `qna_result` block: the RLM's narrative answer, an optional
 * markdown table, optional seaborn PNG images (with zoom lightbox + download),
 * and a collapsible "Show code" panel.
 */
const QnAResultBlock = ({ blockData = {} }) => {
  const { question, headline, narrative, table_md, image_urls = [], code } = blockData;
  const [lightbox, setLightbox] = useState(null);
  const [showCode, setShowCode] = useState(false);

  return (
    <Block icon="search" title="Answer">
      {question && (
        <div style={{
          fontSize: 12, color: 'var(--fg-muted)', marginBottom: 10,
          fontStyle: 'italic',
        }}>
          {question}
        </div>
      )}

      {headline && (
        <div className="q-md" style={{
          fontSize: 17, fontWeight: 600, lineHeight: 1.45, color: 'var(--fg)',
          marginBottom: narrative ? 6 : 0,
        }}>
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownTableComponents}>{headline}</ReactMarkdown>
        </div>
      )}

      {narrative && (
        <div className="q-md" style={{
          fontSize: 13.5, lineHeight: 1.6,
          color: headline ? 'var(--fg-muted)' : 'var(--fg)',
        }}>
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownTableComponents}>{narrative}</ReactMarkdown>
        </div>
      )}

      {table_md && (
        <div className="q-md" style={{
          marginTop: 12, fontSize: 13, color: 'var(--fg)', overflow: 'auto',
        }}>
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownTableComponents}>{table_md}</ReactMarkdown>
        </div>
      )}

      {image_urls.length > 0 && (
        <div style={{ marginTop: 14, display: 'flex', flexWrap: 'wrap', gap: 10 }}>
          {image_urls.map((url) => {
            const resolved = resolveUrl(url);
            return (
              <div key={url} style={{ position: 'relative', display: 'inline-block' }}>
                <img
                  src={resolved}
                  alt="Analysis chart"
                  style={{
                    maxWidth: 480, maxHeight: 360,
                    borderRadius: 8,
                    border: '1px solid var(--line-soft)',
                    cursor: 'zoom-in',
                    display: 'block',
                  }}
                  onClick={() => setLightbox(resolved)}
                />
                <a
                  href={resolved}
                  download
                  onClick={(e) => e.stopPropagation()}
                  title="Download PNG"
                  style={{
                    position: 'absolute', bottom: 6, right: 6,
                    background: 'var(--card)',
                    border: '1px solid var(--border)',
                    borderRadius: 6,
                    padding: '3px 8px',
                    fontSize: 11,
                    color: 'var(--fg-muted)',
                    textDecoration: 'none',
                    lineHeight: 1.4,
                  }}
                >
                  ↓ PNG
                </a>
              </div>
            );
          })}
        </div>
      )}

      {code && (
        <div style={{ marginTop: 12 }}>
          <button
            type="button"
            onClick={() => setShowCode((s) => !s)}
            style={{
              fontSize: 12, color: 'var(--fg-muted)',
              background: 'transparent', border: 'none', cursor: 'pointer',
              padding: 0, textDecoration: 'underline',
            }}
          >
            {showCode ? 'Hide code' : 'Show code'}
          </button>
          {showCode && (
            <pre style={{
              marginTop: 8, padding: 10, fontSize: 12, lineHeight: 1.5,
              background: 'var(--bg-elev-1)', border: '1px solid var(--line-soft)',
              borderRadius: 'var(--r-md)', overflow: 'auto',
              whiteSpace: 'pre-wrap', wordBreak: 'break-word',
              color: 'var(--fg)',
            }}>
              {code}
            </pre>
          )}
        </div>
      )}

      {lightbox && ReactDOM.createPortal(
        <div
          onClick={() => setLightbox(null)}
          style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            background: 'rgba(0,0,0,0.82)',
            display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center',
            gap: 14,
          }}
        >
          <img
            src={lightbox}
            alt="Enlarged chart"
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth: '90vw', maxHeight: '80vh',
              borderRadius: 10,
              border: '1px solid var(--border)',
            }}
          />
          <div style={{ display: 'flex', gap: 12 }}>
            <a
              href={lightbox}
              download
              className="ui-btn ui-btn--outline"
              style={{ background: 'var(--card)', textDecoration: 'none' }}
              onClick={(e) => e.stopPropagation()}
            >
              Download PNG
            </a>
            <Button
              variant="outline"
              style={{ background: 'var(--card)' }}
              onClick={() => setLightbox(null)}
            >
              Close
            </Button>
          </div>
        </div>,
        document.body
      )}
    </Block>
  );
};

export default QnAResultBlock;
